import { describeMutationError } from "@api/mutationErrors";
import type { CaptureNode } from "@capture/captureClient";
import { STATUS_WORD, statusForNode } from "@capture/captureNodeStatus";
import { DISPLAY_KIND_LABEL } from "@displays/displaysClient";
import { ROOM_KIND_LABEL, type Room, type RoomKind } from "@rooms/roomsClient";
import { useCreateRoom, useUpdateRoom } from "@rooms/roomsQueries";
import {
  Button,
  FieldError,
  Input,
  SegmentedControl,
  Select,
} from "@scribenote/brian-design-system";
import { formatRelativeTimeMs } from "@utils/format-date";
import { type ReactNode, useState } from "react";

import {
  AREA_LABEL,
  COVERAGE_LABEL,
  DISPLAY_STATUS_LABEL,
  type DocEdit,
  MAX_BOXES_PER_SPACE,
  type MapArea,
  type MapCoverage,
  type MapDevice,
  type MapDisplay,
  type MapDisplays,
  type MapDocument,
  type MapDoor,
  type MapOutside,
  type MapScreen,
  type MapSpace,
  OUTSIDE_KINDS,
  OUTSIDE_LABEL,
} from "./clinicMapClient";
import type { MapSelection } from "./MapCanvas";
import {
  centre,
  doorJoin,
  doorsOf,
  halfCell,
  insideSpace,
  labelBox,
  screenHome,
  withoutDoor,
} from "./mapGeometry";
import {
  doorLeadsTo,
  doorTitle,
  type RoomsById,
  screenName,
  spaceName,
} from "./mapWords";

const KIND_OPTIONS = (Object.keys(ROOM_KIND_LABEL) as RoomKind[]).map(
  (kind) => ({ value: kind, label: ROOM_KIND_LABEL[kind] }),
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-1">
    <span className="font-mono text-mono-caps text-annotation">{label}</span>
    {children}
  </div>
);

interface EditArgs {
  doc: MapDocument;
  onChange: DocEdit;
}

/** A blank label is no label: brian refuses one that is only spaces. */
const labelOf = (value: string) => (value.trim() ? value : null);

/** Removing a space takes the doors on its walls and the devices and screens in it. */
const withoutSpace = (doc: MapDocument, space: MapSpace): MapDocument => {
  const doors = new Set(doorsOf(doc, space).map((d) => d.uuid));
  return {
    ...doc,
    spaces: doc.spaces.filter((s) => s.uuid !== space.uuid),
    doors: doc.doors.filter((d) => !doors.has(d.uuid)),
    devices: doc.devices.filter((d) => d.space_uuid !== space.uuid),
    screens: doc.screens.filter((s) => s.space_uuid !== space.uuid),
  };
};

/** `onCreated` runs even if the panel moved on while brian created the room. */
const NewRoom = ({ onCreated }: { onCreated: (room: Room) => void }) => {
  const create = useCreateRoom();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<RoomKind>("exam");
  return (
    <div className="flex flex-col gap-2 rounded-paper border border-rule p-2">
      <Input
        label="New room"
        value={name}
        placeholder="Exam 3"
        onChange={(event) => setName(event.target.value)}
      />
      <Select
        label="What it is for"
        value={kind}
        options={KIND_OPTIONS}
        onValueChange={(value) => setKind(value as RoomKind)}
      />
      {create.isError && (
        <FieldError>
          {describeMutationError(create.error, "create the room")}
        </FieldError>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={!name.trim()}
        busy={create.isPending}
        onClick={() =>
          void create
            .mutateAsync({ name: name.trim(), kind })
            .then(onCreated, () => undefined)
        }
      >
        Create room
      </Button>
    </div>
  );
};

const RoomFields = ({
  space,
  rooms,
  roomsById,
  update,
  tie,
  onDrawDoors,
  onDrawAreas,
  mapped,
}: {
  space: MapSpace;
  rooms: readonly Room[];
  roomsById: RoomsById;
  update: (patch: Partial<MapSpace>) => void;
  tie: (room: Room) => void;
  onDrawDoors: () => void;
  onDrawAreas: () => void;
  mapped: ReadonlySet<string>;
}) => {
  const updateRoom = useUpdateRoom();
  const room = space.room_uuid ? roomsById.get(space.room_uuid) : undefined;
  const choices = rooms.filter(
    (r) => r.id === space.room_uuid || (r.active && !mapped.has(r.id)),
  );
  return (
    <>
      <Select
        label="Room"
        value={space.room_uuid ?? null}
        placeholder="Choose a room"
        options={choices.map((r) => ({ value: r.id, label: r.name }))}
        onValueChange={(value) => update({ room_uuid: value })}
      />
      {!room && <NewRoom onCreated={tie} />}
      {room && (
        <>
          <Select
            label="What it is for"
            value={room.kind}
            options={KIND_OPTIONS}
            onValueChange={(value) =>
              updateRoom.mutate({
                roomUuid: room.id,
                change: { kind: value as RoomKind },
              })
            }
          />
          {updateRoom.isError && (
            <FieldError>
              {describeMutationError(updateRoom.error, "change the room")}
            </FieldError>
          )}
          <SegmentedControl<MapCoverage | "default">
            label="Camera"
            aria-label="What the camera sees"
            size="sm"
            value={space.coverage ?? "default"}
            options={[
              { value: "default", label: "Default" },
              { value: "full", label: COVERAGE_LABEL.full },
              { value: "blind_spots", label: COVERAGE_LABEL.blind_spots },
            ]}
            onValueChange={(value) =>
              update({ coverage: value === "default" ? null : value })
            }
          />
          <Input
            label="Keep someone out of view for (minutes)"
            type="number"
            min={1}
            max={240}
            placeholder="15"
            value={space.out_of_view_limit_min ?? ""}
            onChange={(event) => {
              const minutes = Number.parseInt(event.target.value, 10);
              update({
                out_of_view_limit_min: Number.isFinite(minutes)
                  ? Math.min(Math.max(minutes, 1), 240)
                  : null,
              });
            }}
          />
          <Button size="sm" onClick={onDrawDoors}>
            Draw doors on its camera
          </Button>
          {room.kind === "reception" && (
            <Button size="sm" onClick={onDrawAreas}>
              Draw reception areas
            </Button>
          )}
        </>
      )}
    </>
  );
};

const SpaceInspector = ({
  space,
  box,
  doc,
  onChange,
  onSelect,
  rooms,
  roomsById,
  addingBox,
  onAddBox,
  onDrawDoors,
  onDrawAreas,
}: EditArgs & {
  space: MapSpace;
  box: number;
  onSelect: (selection: MapSelection) => void;
  rooms: readonly Room[];
  roomsById: RoomsById;
  addingBox: boolean;
  onAddBox: (adding: boolean) => void;
  onDrawDoors: () => void;
  onDrawAreas: () => void;
}) => {
  const update = (patch: Partial<MapSpace>) =>
    onChange({
      ...doc,
      spaces: doc.spaces.map((s) =>
        s.uuid === space.uuid ? { ...s, ...patch } : s,
      ),
    });
  const mapped = new Set(
    doc.spaces.flatMap((s) => (s.room_uuid ? [s.room_uuid] : [])),
  );
  const doors = doorsOf(doc, space);
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-base text-ink">
        {spaceName(space, roomsById)}
      </h3>
      <SegmentedControl<"room" | "area">
        aria-label="Room or area"
        size="sm"
        value={space.area ? "area" : "room"}
        options={[
          { value: "room", label: "A Brian room" },
          { value: "area", label: "An area" },
        ]}
        onValueChange={(value) =>
          update(
            value === "area"
              ? {
                  room_uuid: null,
                  area: "hallway",
                  label: AREA_LABEL.hallway,
                  coverage: null,
                  out_of_view_limit_min: null,
                }
              : { area: null, label: null },
          )
        }
      />
      {space.area ? (
        <>
          <Select
            label="Kind of area"
            value={space.area}
            options={(Object.keys(AREA_LABEL) as MapArea[]).map((a) => ({
              value: a,
              label: AREA_LABEL[a],
            }))}
            onValueChange={(value) => update({ area: value as MapArea })}
          />
          <Input
            label="Name"
            value={space.label ?? ""}
            onChange={(event) => update({ label: labelOf(event.target.value) })}
          />
        </>
      ) : (
        <RoomFields
          space={space}
          rooms={rooms}
          roomsById={roomsById}
          update={update}
          tie={(r) =>
            onChange((current) => ({
              ...current,
              spaces: current.spaces.map((s) =>
                s.uuid === space.uuid ? { ...s, room_uuid: r.id } : s,
              ),
            }))
          }
          mapped={mapped}
          onDrawDoors={onDrawDoors}
          onDrawAreas={onDrawAreas}
        />
      )}
      <Field label="Doors">
        {doors.length === 0 ? (
          <p className="text-sm text-annotation">
            None yet. Add one with the door tool, or draw it on the camera.
          </p>
        ) : (
          <ul className="flex flex-col">
            {doors.map((door) => (
              <li key={door.uuid}>
                <button
                  type="button"
                  className="w-full py-0.5 text-left text-sm text-ink-body hover:text-ink"
                  onClick={() => onSelect({ kind: "door", uuid: door.uuid })}
                >
                  {doorLeadsTo(doc, door, space, roomsById)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Field>
      <Field label={`Boxes (${space.boxes.length})`}>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={addingBox ? "accent" : "outline"}
            disabled={!addingBox && space.boxes.length >= MAX_BOXES_PER_SPACE}
            onClick={() => onAddBox(!addingBox)}
          >
            {addingBox ? "Done adding" : "Add a box"}
          </Button>
          {space.boxes.length > 1 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                update({ boxes: space.boxes.filter((_, i) => i !== box) });
                onSelect({ kind: "space", uuid: space.uuid, box: 0 });
              }}
            >
              Remove this box
            </Button>
          )}
        </div>
        {addingBox && (
          <p className="text-sm text-annotation">
            Draw a box that shares a wall with this space (an L-shaped room).
          </p>
        )}
      </Field>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          onChange(withoutSpace(doc, space));
          onSelect(null);
        }}
      >
        Remove from the map
      </Button>
    </div>
  );
};

const DoorInspector = ({
  door,
  doc,
  onChange,
  onSelect,
  roomsById,
}: EditArgs & {
  door: MapDoor;
  onSelect: (selection: MapSelection) => void;
  roomsById: RoomsById;
}) => {
  const join = doorJoin(doc, door);
  const update = (patch: Partial<MapDoor>) =>
    onChange({
      ...doc,
      doors: doc.doors.map((d) =>
        d.uuid === door.uuid ? { ...d, ...patch } : d,
      ),
    });
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-base text-ink">
        {doorTitle(doc, door, roomsById)}
      </h3>
      {!join.ok && (
        <FieldError>
          {join.reason === "spans_two_walls"
            ? "This door runs past the end of a wall."
            : join.reason === "inside_space"
              ? "This door sits between two boxes of one space."
              : "This door is not on a wall."}
        </FieldError>
      )}
      <p className="text-sm text-annotation">
        Drag it along its wall to move it.
      </p>
      <SegmentedControl<MapDoor["kind"]>
        aria-label="Door or opening"
        size="sm"
        value={door.kind}
        options={[
          { value: "door", label: "Door" },
          { value: "opening", label: "Opening" },
        ]}
        onValueChange={(kind) => update({ kind })}
      />
      {join.ok && join.to === null && (
        <Select
          label="It leads outside, as"
          value={door.outside ?? null}
          placeholder="Choose what it is"
          error={door.outside ? undefined : "Choose what this door is"}
          options={OUTSIDE_KINDS.map((o) => ({
            value: o,
            label: OUTSIDE_LABEL[o],
          }))}
          onValueChange={(value) => update({ outside: value as MapOutside })}
        />
      )}
      <Input
        label="Label (optional)"
        value={door.label ?? ""}
        placeholder="Front door"
        onChange={(event) => update({ label: labelOf(event.target.value) })}
      />
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          onChange(withoutDoor(doc, door.uuid));
          onSelect(null);
        }}
      >
        Remove door
      </Button>
    </div>
  );
};

const DeviceInspector = ({
  device,
  node,
  doc,
  onChange,
  onSelect,
}: EditArgs & {
  device: MapDevice;
  node: CaptureNode | undefined;
  onSelect: (selection: MapSelection) => void;
}) => {
  const update = (patch: Partial<MapDevice>) =>
    onChange({
      ...doc,
      devices: doc.devices.map((d) =>
        d.instance_id === device.instance_id ? { ...d, ...patch } : d,
      ),
    });
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-base text-ink">
        {node?.roomName ?? "Device"}
      </h3>
      <p className="font-mono text-xs text-annotation">
        {device.instance_id}
        {node && ` · ${STATUS_WORD[statusForNode(node)].label}`}
      </p>
      <p className="text-sm text-annotation">Drag it to where it sits.</p>
      <Input
        label={`Faces (${device.heading_deg}° from up, clockwise)`}
        type="range"
        min={0}
        max={345}
        step={15}
        value={device.heading_deg}
        onChange={(event) =>
          update({ heading_deg: Number(event.target.value) })
        }
      />
      <Input
        label={`Sees ${device.fov_deg}° wide`}
        type="range"
        min={30}
        max={180}
        step={10}
        value={device.fov_deg}
        onChange={(event) => update({ fov_deg: Number(event.target.value) })}
      />
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          onChange({
            ...doc,
            devices: doc.devices.filter(
              (d) => d.instance_id !== device.instance_id,
            ),
          });
          onSelect(null);
        }}
      >
        Remove from the map
      </Button>
    </div>
  );
};

const Devices = ({
  doc,
  onChange,
  onSelect,
  nodes,
}: EditArgs & {
  nodes: readonly CaptureNode[];
  onSelect: (selection: MapSelection) => void;
}) => {
  const placed = new Set(doc.devices.map((d) => d.instance_id));
  return (
    <Field label="Devices">
      {nodes.length === 0 ? (
        <p className="text-sm text-annotation">No capture devices yet.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {nodes.map((node) => {
            const space = node.roomUuid
              ? doc.spaces.find((s) => s.room_uuid === node.roomUuid)
              : undefined;
            return (
              <li
                key={node.instanceId}
                className="flex min-w-0 items-center justify-between gap-2"
              >
                <span className="min-w-0 truncate text-sm text-ink-body">
                  {node.roomName}
                  <span className="ml-1 font-mono text-xs text-annotation">
                    {node.instanceId}
                  </span>
                </span>
                {placed.has(node.instanceId) ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      onSelect({ kind: "device", instanceId: node.instanceId })
                    }
                  >
                    Placed
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!space}
                    title={space ? undefined : "Put its room on the map first"}
                    onClick={() => {
                      if (!space) return;
                      const at = centre(labelBox(space));
                      onChange({
                        ...doc,
                        devices: [
                          ...doc.devices,
                          {
                            instance_id: node.instanceId,
                            space_uuid: space.uuid,
                            x: halfCell(at.x),
                            y: halfCell(at.y),
                            heading_deg: 0,
                            fov_deg: 90,
                          },
                        ],
                      });
                      onSelect({ kind: "device", instanceId: node.instanceId });
                    }}
                  >
                    Place
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Field>
  );
};

const statusLine = (display: MapDisplay) =>
  [
    DISPLAY_KIND_LABEL[display.kind],
    DISPLAY_STATUS_LABEL[display.status],
    display.last_seen_at &&
      `seen ${formatRelativeTimeMs(Date.parse(display.last_seen_at))}`,
  ]
    .filter(Boolean)
    .join(" · ");

const ScreenInspector = ({
  screen,
  display,
  doc,
  onChange,
  onSelect,
}: EditArgs & {
  screen: MapScreen;
  display: MapDisplay | undefined;
  onSelect: (selection: MapSelection) => void;
}) => (
  <div className="flex flex-col gap-3">
    <h3 className="font-display text-base text-ink">{screenName(display)}</h3>
    {display && (
      <p className="font-mono text-xs text-annotation">{statusLine(display)}</p>
    )}
    <p className="text-sm text-annotation">
      {display?.home_room_uuid
        ? "Drag it to where it hangs in its room."
        : "Drag it to where it hangs."}
    </p>
    <Input
      label={`Faces (${screen.heading_deg}° from up, clockwise)`}
      type="range"
      min={0}
      max={345}
      step={15}
      value={screen.heading_deg}
      onChange={(event) =>
        onChange({
          ...doc,
          screens: doc.screens.map((s) =>
            s.display_uuid === screen.display_uuid
              ? { ...s, heading_deg: Number(event.target.value) }
              : s,
          ),
        })
      }
    />
    <Button
      size="sm"
      variant="ghost"
      onClick={() => {
        onChange({
          ...doc,
          screens: doc.screens.filter(
            (s) => s.display_uuid !== screen.display_uuid,
          ),
        });
        onSelect(null);
      }}
    >
      Remove from the map
    </Button>
  </div>
);

/** Below the space's name, clear of a device placed on it, when the space is tall enough. */
const withScreen = (
  doc: MapDocument,
  display: MapDisplay,
  space: MapSpace,
): MapDocument => {
  const at = centre(labelBox(space));
  const below = { x: halfCell(at.x), y: halfCell(at.y + 1.5) };
  const point = insideSpace(below, space)
    ? below
    : { x: halfCell(at.x), y: halfCell(at.y) };
  return {
    ...doc,
    screens: [
      ...doc.screens,
      {
        display_uuid: display.display_uuid,
        space_uuid: space.uuid,
        ...point,
        heading_deg: 0,
      },
    ],
  };
};

const Screens = ({
  doc,
  onChange,
  onSelect,
  displays,
}: EditArgs & {
  displays: MapDisplays;
  onSelect: (selection: MapSelection) => void;
}) => {
  const placed = new Set(doc.screens.map((s) => s.display_uuid));
  const select = (display: MapDisplay) =>
    onSelect({ kind: "screen", displayUuid: display.display_uuid });
  return (
    <Field label="Screens">
      {displays.size === 0 ? (
        <p className="text-sm text-annotation">
          No screens yet. Add them in Displays.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {[...displays.values()].map((display) => {
            const space = screenHome(doc, display.home_room_uuid);
            return (
              <li
                key={display.display_uuid}
                className="flex min-w-0 items-center justify-between gap-2"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm text-ink-body">
                    {display.name}
                  </span>
                  <span className="truncate font-mono text-xs text-annotation">
                    {statusLine(display)}
                  </span>
                </span>
                {placed.has(display.display_uuid) ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => select(display)}
                  >
                    Placed
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!space}
                    title={
                      space
                        ? undefined
                        : display.home_room_uuid
                          ? "Put its room on the map first"
                          : "Draw a space first"
                    }
                    onClick={() => {
                      if (!space) return;
                      onChange(withScreen(doc, display, space));
                      select(display);
                    }}
                  >
                    Place
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Field>
  );
};

export const MapInspector = ({
  doc,
  onChange,
  selection,
  onSelect,
  rooms,
  roomsById,
  nodes,
  displays,
  addBoxTo,
  onAddBoxTo,
  onDrawDoors,
  onDrawAreas,
}: EditArgs & {
  selection: MapSelection;
  onSelect: (selection: MapSelection) => void;
  rooms: readonly Room[];
  roomsById: RoomsById;
  nodes: readonly CaptureNode[];
  displays: MapDisplays;
  addBoxTo: string | null;
  onAddBoxTo: (space: string | null) => void;
  onDrawDoors: (space: MapSpace) => void;
  onDrawAreas: (space: MapSpace) => void;
}) => {
  if (selection?.kind === "space") {
    const space = doc.spaces.find((s) => s.uuid === selection.uuid);
    if (space)
      return (
        <SpaceInspector
          key={space.uuid}
          space={space}
          box={selection.box}
          doc={doc}
          onChange={onChange}
          onSelect={onSelect}
          rooms={rooms}
          roomsById={roomsById}
          addingBox={addBoxTo === space.uuid}
          onAddBox={(adding) => onAddBoxTo(adding ? space.uuid : null)}
          onDrawDoors={() => onDrawDoors(space)}
          onDrawAreas={() => onDrawAreas(space)}
        />
      );
  }
  if (selection?.kind === "door") {
    const door = doc.doors.find((d) => d.uuid === selection.uuid);
    if (door)
      return (
        <DoorInspector
          door={door}
          doc={doc}
          onChange={onChange}
          onSelect={onSelect}
          roomsById={roomsById}
        />
      );
  }
  if (selection?.kind === "device") {
    const device = doc.devices.find(
      (d) => d.instance_id === selection.instanceId,
    );
    if (device)
      return (
        <DeviceInspector
          device={device}
          node={nodes.find((n) => n.instanceId === device.instance_id)}
          doc={doc}
          onChange={onChange}
          onSelect={onSelect}
        />
      );
  }
  if (selection?.kind === "screen") {
    const screen = doc.screens.find(
      (s) => s.display_uuid === selection.displayUuid,
    );
    if (screen)
      return (
        <ScreenInspector
          key={screen.display_uuid}
          screen={screen}
          display={displays.get(screen.display_uuid)}
          doc={doc}
          onChange={onChange}
          onSelect={onSelect}
        />
      );
  }
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-body">
        Draw a box for each room and area, tie each box to its room, then draw
        the room&rsquo;s doors on its camera. Doors the camera can&rsquo;t see
        go on the map with the door tool.
      </p>
      <Devices
        doc={doc}
        onChange={onChange}
        onSelect={onSelect}
        nodes={nodes}
      />
      <Screens
        doc={doc}
        onChange={onChange}
        onSelect={onSelect}
        displays={displays}
      />
    </div>
  );
};
